/**
 * Edit Device Form Schema
 * 
 * Defines the validation for device updates.
 * deviceNumber is provided as a parameter.
 * 
 * Form metadata (name, title, description) is loaded from form.yml
 */

import { z } from 'zod'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

// ============================================================================
// Export Form Metadata from YAML
// ============================================================================

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

// ============================================================================
// Constants
// ============================================================================

const MIN_NUMBER = 1000000000
const MAX_NUMBER = 9999999999

// ============================================================================
// Enum Types (matching API schema)
// ============================================================================

// All possible device statuses — must match the backend upsertDevice contract
// (device.deviceStatus enum). A device's CURRENT status is fed into this schema as
// the default value, so any status the backend allows must validate here; otherwise
// devices in those states (e.g. shipped/testing/tested) can't be edited at all.
export const AllDeviceStatusEnum = z.enum([
  'ordered',
  'shipped',
  'received',
  'testing',
  'tested',
  'failed',
  'available',
  'live',
  'provisioned',
  'retired',
  'faulty',
  'returned',
  'procured',
])

// Statuses available for device updates
export const DeviceStatusEnum = AllDeviceStatusEnum
export type DeviceStatus = z.infer<typeof DeviceStatusEnum>

// ============================================================================
// Form Schema (What user fills in)
// ============================================================================

export const EditDeviceFormSchema = z.object({
  // Device identifier (provided as parameter)
  deviceNumber: z
    .number()
    .gte(MIN_NUMBER)
    .lte(MAX_NUMBER),

  // User-friendly name for the device.
  // Optional to match the backend (upsertDevice: device.deviceName is optional,
  // minLength 1 / maxLength 255 only if present). Devices can be ordered without
  // a name, so requiring it here silently blocks editing those devices.
  deviceName: z
    .string()
    .max(255, 'Name cannot exceed 255 characters')
    .optional()
    .or(z.literal('')),

  // UI-only: used to filter device types (not sent to API).
  // Select controls emit strings, so coerce — otherwise the form-level schema
  // validator rejects the value and the Save button stays disabled.
  manufacturerNumber: z.preprocess(
    (v) => (v === '' || v == null ? undefined : v),
    z.coerce.number()
      .gte(MIN_NUMBER)
      .lte(MAX_NUMBER)
      .optional(),
  ),

  // Optional: links device to a device profile (Feature 6). Sent on the device;
  // backend persistence pending (device→profile link hand-off).
  deviceProfileNumber: z
    .coerce.number()
    .gte(MIN_NUMBER)
    .lte(MAX_NUMBER)
    .optional(),

  // Links device to a device type. Optional/nullable to match the backend
  // (upsertDevice: device.deviceTypeNumber is optional). Devices without a type
  // must still be editable. Empty selection ('') is treated as "not set".
  deviceTypeNumber: z.preprocess(
    (v) => (v === '' || v == null ? undefined : v),
    z.coerce.number()
      .gte(MIN_NUMBER, 'Invalid device type')
      .lte(MAX_NUMBER, 'Invalid device type')
      .optional(),
  ),

  // Optional: where the device was purchased.
  distributorNumber: z.preprocess(
    (v) => (v === '' || v == null ? undefined : v),
    z.coerce.number()
      .gte(MIN_NUMBER)
      .lte(MAX_NUMBER)
      .optional(),
  ),

  // UI-only: a device is ordered from EITHER a manufacturer OR a distributor.
  // Controls which single source field is shown (not sent to the API).
  orderedFrom: z.enum(['manufacturer', 'distributor']).optional(),

  // Required: current status of the device
  deviceStatus: DeviceStatusEnum,
})

export type EditDeviceFormData = z.infer<typeof EditDeviceFormSchema>

/**
 * Transform form data to API request format for updates.
 * Converts the flat form structure to the nested API structure.
 */
export function transformToApiRequest(formData: EditDeviceFormData) {
  const deviceName = formData.deviceName?.trim()

  return {
    device: {
      deviceNumber: formData.deviceNumber,
      deviceStatus: formData.deviceStatus,
      // Only send a name when non-empty — backend rejects deviceName "" (optional,
      // but minLength 1 if present).
      ...(deviceName ? { deviceName } : {}),
      ...(formData.deviceTypeNumber ? { deviceTypeNumber: formData.deviceTypeNumber } : {}),
      // Preserve the device→profile link (Feature 6) when set.
      ...(formData.deviceProfileNumber ? { deviceProfileNumber: formData.deviceProfileNumber } : {}),
    },
    // Only touch procurementInfo when there is something to set, so editing a
    // device's name/status/type doesn't create an empty procurement record.
    ...(formData.distributorNumber
      ? {
          procurementInfo: {
            deviceDistributorNumber: formData.distributorNumber,
            ...(formData.deviceTypeNumber ? { deviceTypeNumber: formData.deviceTypeNumber } : {}),
          },
        }
      : {}),
  }
}
